import {
  AwaitContainer,
  Button,
  DialogRoot,
  DialogTrigger,
  Item,
  ItemActions,
  ItemDescription,
  ItemHeader,
  ItemTitle,
} from '@ibsys/kit';
import { XCircleIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTokenStatus,
  ServiceTokenCreateResponse,
  ServiceTokenListItem,
  useCreateServiceToken,
  useRevokeServiceToken,
  useServiceTokens,
} from '@/entities/serviceTokens';
import { toastError, toastSuccess } from '@/shared/lib/utils/toast';
import { CreateServiceTokenDialog } from './CreateServiceTokenDialog';
import { RevokeTokenDialog } from './RevokeTokenDialog';
import { TokenCreatedDialog } from './TokenCreatedDialog';

type Props = {
  userId: number;
  disabled?: boolean;
};

type DialogState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'created'; token: string }
  | { type: 'revoke'; token: ServiceTokenListItem };

export const ServiceTokensList: FC<Props> = ({ userId, disabled }) => {
  const { t, i18n } = useTranslation(['users', 'common']);
  const [dialogState, setDialogState] = useState<DialogState>({ type: 'none' });

  const { data, isLoading } = useServiceTokens(userId);
  const { mutateAsync: createToken, isPending: isCreating } = useCreateServiceToken(userId);
  const { mutateAsync: revokeToken, isPending: isRevoking } = useRevokeServiceToken(userId);

  // Sort: active first, then by creation date (newest first), revoked at the end
  const sortedTokens = useMemo(() => {
    if (!data?.tokens) return [];
    return [...data.tokens].sort((a, b) => {
      const statusA = getTokenStatus(a);
      const statusB = getTokenStatus(b);

      // Revoked tokens at the end
      if (statusA === 'revoked' && statusB !== 'revoked') return 1;
      if (statusA !== 'revoked' && statusB === 'revoked') return -1;

      // Sort by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [data?.tokens]);

  const handleOpenCreate = () => setDialogState({ type: 'create' });
  const handleCloseDialog = () => setDialogState({ type: 'none' });

  const handleCreateToken = async (formData: { description: string; expiredAt?: string }) => {
    try {
      const result: ServiceTokenCreateResponse = await createToken({
        description: formData.description,
        expiredAt: formData.expiredAt,
      });
      setDialogState({ type: 'created', token: result.token });
    } catch {
      toastError(t('common:error'));
    }
  };

  const handleOpenRevoke = (token: ServiceTokenListItem) => {
    setDialogState({ type: 'revoke', token });
  };

  const handleRevokeToken = async () => {
    if (dialogState.type !== 'revoke') return;

    try {
      await revokeToken(dialogState.token.id);
      toastSuccess(t('tokenRevoked'));
      setDialogState({ type: 'none' });
    } catch {
      toastError(t('common:error'));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusDisplay = (token: ServiceTokenListItem) => {
    const status = getTokenStatus(token);
    switch (status) {
      case 'active':
        return {
          label: token.expiredAt ? t('validUntil', { date: formatDate(token.expiredAt) }) : t('tokenStatusActive'),
          className: 'text-green-600',
        };
      case 'expired':
        return {
          label: t('tokenStatusExpired'),
          className: 'text-yellow-600',
        };
      case 'revoked':
        return {
          label: t('tokenStatusRevoked'),
          className: 'text-muted-foreground',
        };
    }
  };

  const isDialogOpen = dialogState.type !== 'none';

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-5 items-center">
          <p className="font-medium">{t('serviceTokens')}</p>

          {!disabled && (
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleOpenCreate}>
                {t('createToken')}
              </Button>
            </DialogTrigger>
          )}
        </div>

        <AwaitContainer isLoading={isLoading}>
          <div className="flex flex-col gap-3">
            {sortedTokens.map((token) => {
              const status = getTokenStatus(token);
              const statusDisplay = getStatusDisplay(token);
              const isActive = status === 'active';

              return (
                <Item
                  variant="outline"
                  size="sm"
                  key={token.id}
                  className={status === 'revoked' ? 'opacity-60' : undefined}
                >
                  <ItemHeader>
                    <div className="flex flex-col gap-1 flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-muted-foreground">
                          {t('tokenPrefixShort', { prefix: token.tokenPrefix })}
                        </span>
                        <ItemTitle className="truncate">{token.description}</ItemTitle>
                      </div>
                      <ItemDescription className={statusDisplay.className}>{statusDisplay.label}</ItemDescription>
                    </div>
                    <ItemActions className="ml-4">
                      {!disabled && isActive && (
                        <Button
                          icon={<XCircleIcon />}
                          variant="ghost"
                          color="destructive"
                          aria-label={t('users:revoke')}
                          onClick={() => handleOpenRevoke(token)}
                        />
                      )}
                    </ItemActions>
                  </ItemHeader>
                </Item>
              );
            })}
            {sortedTokens.length === 0 && <p className="text-sm text-muted-foreground">{t('noTokens')}</p>}
          </div>
        </AwaitContainer>
      </div>

      {dialogState.type === 'create' && (
        <CreateServiceTokenDialog onCancel={handleCloseDialog} onSubmit={handleCreateToken} isLoading={isCreating} />
      )}

      {dialogState.type === 'created' && <TokenCreatedDialog token={dialogState.token} onClose={handleCloseDialog} />}

      {dialogState.type === 'revoke' && (
        <RevokeTokenDialog
          token={dialogState.token}
          onConfirm={handleRevokeToken}
          onCancel={handleCloseDialog}
          isLoading={isRevoking}
        />
      )}
    </DialogRoot>
  );
};
